import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';

type MonitoringPayload = {
  type: string;
  value?: number;
  id?: string;
  delta?: number;
  rating?: string;
  message?: string;
  stack?: string;
  url: string;
  userAgent: string;
  timestamp: string;
};

const endpoint = import.meta.env.VITE_OBSERVABILITY_ENDPOINT || '/api/observability';
const observabilityToken = import.meta.env.VITE_OBSERVABILITY_TOKEN;

// Report only the route (origin + path) to telemetry. The query string can
// carry `?d=` share state that encodes the user's calculator inputs (balances,
// income, spending); those must never leave the browser, so we strip the query
// and hash before sending. Keep this in sync with the privacy copy on /about-us.
function currentRouteUrl(): string {
  try {
    const { origin, pathname } = window.location;
    return `${origin}${pathname}`;
  } catch {
    return '';
  }
}

function sendMonitoringEvent(payload: MonitoringPayload): void {
  if (!endpoint) {
    return;
  }

  const body = JSON.stringify(payload);

  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon(endpoint, blob);
    return;
  }

  void fetch(endpoint, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(observabilityToken ? { 'x-observability-token': observabilityToken } : {})
    },
    body,
    keepalive: true
  });
}

function emitMetric(metric: Metric): void {
  sendMonitoringEvent({
    type: `web-vital:${metric.name}`,
    value: metric.value,
    delta: metric.delta,
    rating: metric.rating,
    id: metric.id,
    url: currentRouteUrl(),
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString()
  });
}

function emitError(type: string, message: string, stack?: string): void {
  sendMonitoringEvent({
    type,
    message,
    stack,
    url: currentRouteUrl(),
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString()
  });
}

export function trackObservabilityEvent(
  type: string,
  detail?: Record<string, unknown>
): void {
  if (!type || !type.trim()) {
    return;
  }

  let message: string | undefined;
  if (detail) {
    try {
      message = JSON.stringify(detail);
    } catch {
      message = 'event_detail_unserializable';
    }
  }

  sendMonitoringEvent({
    type,
    message,
    url: currentRouteUrl(),
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString()
  });
}

export function initObservability(): void {
  onCLS(emitMetric);
  onFCP(emitMetric);
  onINP(emitMetric);
  onLCP(emitMetric);
  onTTFB(emitMetric);

  window.addEventListener('error', (event) => {
    emitError('runtime:error', event.message, event.error?.stack);
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    const stack = reason instanceof Error ? reason.stack : undefined;
    emitError('runtime:unhandledrejection', message, stack);
  });
}
